/**
 * Contrato de columnas y plantilla de Excel del importador de Ventas de Libros Digitales.
 *
 * Sin React ni antd a propósito: el workbook se puede generar y revisar con un
 * `node script.js`, sin levantar la app.
 *
 * La hoja de datos va PRIMERO porque el importador siempre lee la primera hoja del archivo
 * que se sube: si las instrucciones estuvieran antes, la plantilla llenada a mano se subiría
 * vacía.
 */

import * as XLSXStyle from "xlsx-js-style";
import { border, headerStyle } from "../../../../../Components/ExcelEstilos";

const COLOR_TITULO = "007A8A";
const COLOR_SUAVE = "D6F4F8";

/**
 * Fuente única de la verdad de las columnas: de aquí salen tanto la detección de encabezados
 * de la pantalla como las hojas de la plantilla.
 *
 * `prefijo` va normalizado (sin acentos, en minúsculas, sin signos). Las columnas se
 * reconocen por su prefijo y no por el texto exacto porque los archivos que arma la escuela
 * traen encabezados largos, con saltos de línea y emoji, y cambian cada semestre.
 *
 * "numero de control" y "numero de lote" comparten las dos primeras palabras, pero se
 * comparan con el prefijo COMPLETO, así que no se confunden.
 */
export const COLUMNAS_ESPERADAS = [
  {
    clave: "matricula",
    prefijo: "numero de control",
    etiqueta: "NÚMERO DE CONTROL",
    ancho: 22,
    ejemplo: "EJEMPLO-001",
    ayuda:
      "Matrícula del alumno. Es lo único con lo que se le identifica: si no coincide con ninguna ficha de alumno, la fila no se procesa.",
  },
  {
    clave: "lote",
    prefijo: "numero de lote",
    etiqueta: "NÚMERO DE LOTE",
    ancho: 24,
    ejemplo: "LV-2026-00871",
    ayuda:
      "El código del libro digital que el alumno ya tiene en la mano. Es la llave de todo el proceso: con él se busca la venta. Un renglón por cada código: si el alumno tiene dos libros, aparece dos veces.",
  },
  {
    clave: "correo",
    prefijo: "correo electronico",
    etiqueta: "CORREO ELECTRÓNICO",
    ancho: 32,
    ejemplo: "ana.perez@correo.com",
    ayuda:
      "A dónde se manda el código. También se manda a los correos que el alumno ya tenga en su ficha y, si éste es nuevo, se le guarda. Si se deja vacío se usan sólo los de su ficha.",
  },
  {
    clave: "nombre",
    prefijo: "nombre",
    etiqueta: "NOMBRE(S)",
    ancho: 20,
    ejemplo: "Ana Sofía",
    ayuda:
      "Informativo. El nombre que se usa en la venta es el de la ficha del alumno, no el que venga en el archivo.",
  },
  {
    clave: "apellidoPaterno",
    prefijo: "apellido paterno",
    etiqueta: "APELLIDO PATERNO",
    ancho: 20,
    ejemplo: "Pérez",
    ayuda: "Informativo, igual que el nombre.",
  },
  {
    clave: "apellidoMaterno",
    prefijo: "apellido materno",
    etiqueta: "APELLIDO MATERNO",
    ancho: 20,
    ejemplo: "López",
    ayuda: "Informativo, igual que el nombre.",
  },
  {
    clave: "material",
    prefijo: "material",
    etiqueta: "MATERIAL",
    ancho: 30,
    ejemplo: "Four Corners 3",
    ayuda:
      "Informativo. El libro sale del código, no de este texto: el alumno ya tiene ese libro en la mano.",
  },
];

//El correo NO es obligatorio: si viene vacío se usan los de la ficha del alumno.
export const COLUMNAS_OBLIGATORIAS = ["matricula", "lote"];

export const NOMBRE_PLANTILLA = "plantillaVentasLibrosDigitales.xlsx";

// ─── Estilos ─────────────────────────────────────────────────────────────────

const tituloStyle = {
  font: { bold: true, sz: 13, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: COLOR_TITULO } },
  alignment: { horizontal: "left", vertical: "center" },
};

const subtituloStyle = {
  font: { bold: true, sz: 11, color: { rgb: "0B4F5A" } },
  fill: { fgColor: { rgb: COLOR_SUAVE } },
  alignment: { vertical: "center", wrapText: true },
};

const notaStyle = {
  font: { sz: 10, color: { rgb: "434343" } },
  alignment: { vertical: "center", wrapText: true },
};

const avisoStyle = {
  font: { bold: true, sz: 10, color: { rgb: "A8071A" } },
  alignment: { vertical: "center", wrapText: true },
};

const celdaStyle = { border, alignment: { vertical: "center", wrapText: true } };

const celdaCentradaStyle = {
  border,
  alignment: { vertical: "center", horizontal: "center" },
};

// La fila de ejemplo va en gris cursivo para que se lea como lo que es: algo que hay que
// borrar antes de subir el archivo.
const ejemploStyle = {
  font: { italic: true, sz: 10, color: { rgb: "8C8C8C" } },
  border,
  alignment: { vertical: "center", wrapText: true },
};

const faltaStyle = {
  font: { bold: true, sz: 10, color: { rgb: "A8071A" } },
  border,
  alignment: { vertical: "center", horizontal: "center" },
};

const okStyle = {
  font: { sz: 10, color: { rgb: "237804" } },
  border,
  alignment: { vertical: "center", horizontal: "center" },
};

// ─── Celdas ──────────────────────────────────────────────────────────────────

const celda = (valor, estilo = celdaStyle) => ({
  v: valor === undefined || valor === null ? "" : String(valor),
  t: "s",
  s: estilo,
});

const hojaConEncabezado = (encabezados, filas, anchos) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([
    encabezados.map((titulo) => celda(titulo, headerStyle)),
    ...filas,
  ]);
  hoja["!cols"] = anchos.map((wch) => ({ wch }));
  hoja["!rows"] = [{ hpt: 26 }];
  return hoja;
};

// ─── Hojas ───────────────────────────────────────────────────────────────────

/**
 * Hoja 1: el formato que se sube. Encabezados en la primera fila (el importador los lee de
 * ahí) y dos filas de ejemplo, para que se vea que un alumno con dos libros va en dos
 * renglones repitiendo la matrícula.
 */
const hojaAlumnos = () => {
  const ejemplo = (lote, material) =>
    COLUMNAS_ESPERADAS.map((columna) => {
      if (columna.clave === "lote") return celda(lote, ejemploStyle);
      if (columna.clave === "material") return celda(material, ejemploStyle);
      return celda(columna.ejemplo, ejemploStyle);
    });

  return hojaConEncabezado(
    COLUMNAS_ESPERADAS.map((columna) => columna.etiqueta),
    [
      ejemplo("LV-2026-00871", "Four Corners 3"),
      ejemplo("LV-2026-00872", "Workbook 3"),
    ],
    COLUMNAS_ESPERADAS.map((columna) => columna.ancho),
  );
};

const LINEAS_INSTRUCCIONES = [
  [tituloStyle, "IMPORTAR VENTAS DE LIBROS DIGITALES"],
  [notaStyle, "Panel → Ventas → botón Importar Libros Digitales."],
  [notaStyle, ""],
  [subtituloStyle, "Para qué sirve"],
  [
    notaStyle,
    "Muchos libros digitales se venden en el mostrador a Público en General (u otro cliente genérico) y no queda registro de qué alumno se llevó cada código. Este archivo cierra ese hueco: con el número de lote (que es el código que el alumno tiene en la mano) se localiza la venta y cada matrícula termina con su propia venta.",
  ],
  [notaStyle, ""],
  [subtituloStyle, "Qué hace con cada renglón"],
  [
    notaStyle,
    "Si el código TODAVÍA NO SE HA VENDIDO: le crea la venta al alumno, le entrega ese código exacto descontándolo del inventario y se lo manda por correo.",
  ],
  [
    notaStyle,
    "Si el código YA SE VENDIÓ al cliente de origen y esa venta sólo trae códigos de ese alumno: le cambia el cliente a la venta y le manda el correo con el código.",
  ],
  [
    notaStyle,
    "Si esa venta trae también códigos de OTROS alumnos: los del alumno se separan a una venta nueva a su nombre (con su propio folio de remisión consecutivo), con su parte del cobro, y sólo recibe sus códigos. La venta original se queda con lo demás.",
  ],
  [
    notaStyle,
    "Si la venta YA ES DEL MISMO ALUMNO: no toca nada, sólo le reenvía el correo. Por eso puedes volver a subir el mismo archivo sin miedo a duplicar ventas.",
  ],
  [notaStyle, ""],
  [subtituloStyle, "Qué NO hace (y te lo reporta)"],
  [
    avisoStyle,
    "Si la venta es de OTRO cliente, no le cambia el cliente. Te la reporta con su folio, su cliente y su fecha para que la revises a mano.",
  ],
  [
    avisoStyle,
    "Si la venta ya está TIMBRADA, tampoco. No se puede cambiar el receptor de un CFDI: hay que cancelarlo y refacturarlo.",
  ],
  [
    avisoStyle,
    "Si la venta ya entró en una FACTURA GLOBAL, tampoco. Ya se le facturó al público en general; primero hay que sacarla de la global.",
  ],
  [
    avisoStyle,
    "Una venta con códigos de varios alumnos NO se separa si tiene un cobro parcial, si se cobró con varias formas de pago o si tiene retenciones: no se puede saber qué parte del cobro le toca a cada quien.",
  ],
  [
    notaStyle,
    "Todas esas filas salen en el Excel de resultados que se descarga al terminar.",
  ],
  [notaStyle, ""],
  [subtituloStyle, "Cómo se llena"],
  [
    notaStyle,
    "Un renglón por CÓDIGO, no por alumno. Si un alumno tiene dos libros, va dos veces repitiendo su matrícula: sus dos códigos se juntan en una sola venta y recibe un solo correo con los dos.",
  ],
  [
    notaStyle,
    'Las columnas se reconocen por su encabezado, no por su posición (ver la hoja "Columnas"). Sólo la matrícula y el número de lote son obligatorios.',
  ],
  [
    avisoStyle,
    "Borra las filas de ejemplo antes de subir el archivo: esa matrícula y esos códigos no existen y se reportan como error.",
  ],
  [
    notaStyle,
    'El importador siempre lee la PRIMERA hoja del archivo. No cambies el orden de las hojas ni agregues otra antes de "Alumnos".',
  ],
  [notaStyle, ""],
  [subtituloStyle, "Antes de importar"],
  [
    notaStyle,
    "Elige arriba de la tabla el cliente de origen (con el que se registraron las ventas de los códigos, normalmente Público en General), el Proyecto, el Banco y la Forma de Pago. El banco tiene que ser en pesos.",
  ],
  [
    notaStyle,
    "El proyecto tiene que ser el mismo en el que está el código; si no, la fila se reporta y no se procesa.",
  ],
  [
    notaStyle,
    'Revisa la vista previa: la columna "Qué va a pasar" dice, fila por fila, exactamente lo que va a hacer con cada código.',
  ],
  [
    notaStyle,
    'La hoja "Libros Digitales" te dice qué libros están listos. Uno sin "Lotes y caducidades" NO puede recibir un código específico.',
  ],
  [notaStyle, ""],
  [
    notaStyle,
    "La importación corre en el servidor y puede tardar varios minutos. Puedes salir de la pantalla o cerrar la pestaña: sigue sola y al volver ves su avance. Con Detener se para limpio; al volver a subir el archivo sólo se procesa lo que faltó.",
  ],
];

const hojaInstrucciones = () => {
  const hoja = XLSXStyle.utils.aoa_to_sheet(
    LINEAS_INSTRUCCIONES.map(([estilo, texto]) => [celda(texto, estilo)]),
  );
  hoja["!cols"] = [{ wch: 118 }];
  hoja["!rows"] = LINEAS_INSTRUCCIONES.map(([estilo]) =>
    estilo === tituloStyle ? { hpt: 26 } : { hpt: 16 },
  );
  return hoja;
};

const hojaColumnas = () =>
  hojaConEncabezado(
    [
      "Columna",
      "Obligatoria",
      "Se reconoce si el encabezado empieza con",
      "Para qué sirve",
    ],
    COLUMNAS_ESPERADAS.map((columna) => [
      celda(columna.etiqueta),
      celda(
        COLUMNAS_OBLIGATORIAS.includes(columna.clave) ? "Sí" : "No",
        COLUMNAS_OBLIGATORIAS.includes(columna.clave) ? faltaStyle : celdaCentradaStyle,
      ),
      celda(columna.prefijo),
      celda(columna.ayuda),
    ]),
    [26, 13, 40, 78],
  );

/**
 * Catálogo de consulta: qué libros están marcados como Libro Virtual y si están listos para
 * recibir un código específico.
 *
 * "Lotes y caducidades" es la columna que más importa y la menos evidente: con esa bandera
 * apagada el descuento de inventario ni siquiera mira los lotes, así que el código que se
 * indique en el archivo se ignoraría y el alumno recibiría otro.
 */
const hojaLibros = (catalogos) => {
  const insumos = catalogos?.insumosLibro || [];

  const filas =
    insumos.length > 0
      ? insumos.map((insumo) => [
          celda(insumo.clave),
          celda(insumo.descripcion),
          insumo.codigoSatId ? celda("Sí", okStyle) : celda("FALTA", faltaStyle),
          insumo.venta ? celda("Sí", okStyle) : celda("No", faltaStyle),
          insumo.lotesCaducidades
            ? celda("Sí", okStyle)
            : celda("FALTA", faltaStyle),
        ])
      : [
          [
            celda("(no hay insumos marcados como Libro Virtual)"),
            celda(""),
            celda(""),
            celda(""),
            celda(""),
          ],
        ];

  return hojaConEncabezado(
    [
      "Clave",
      "Descripción",
      "¿Tiene código del SAT?",
      "¿Es de venta?",
      "¿Lotes y caducidades?",
    ],
    filas,
    [18, 46, 18, 14, 20],
  );
};

// ─── Workbook ────────────────────────────────────────────────────────────────

export const construirPlantilla = (catalogos) => {
  const libro = XLSXStyle.utils.book_new();

  XLSXStyle.utils.book_append_sheet(libro, hojaAlumnos(), "Alumnos");
  XLSXStyle.utils.book_append_sheet(libro, hojaInstrucciones(), "Cómo funciona");
  XLSXStyle.utils.book_append_sheet(libro, hojaColumnas(), "Columnas");
  XLSXStyle.utils.book_append_sheet(libro, hojaLibros(catalogos), "Libros Digitales");

  return libro;
};
